/**
 * The Orb's two gestures live in one timer so Fan and Rail cannot drift.
 *
 *   Tap   → existing submenu in place (fan toggles; rail is already open)
 *   Hold  → Hottie
 *
 * A press that reaches HOLD_MS is Hottie and nothing else. The release that
 * follows must not also count as a tap.
 */
import { useCallback, useEffect, useRef } from 'react';

/** How long a press has to hold before it means Hottie rather than the submenu. */
export const HOLD_MS = 450;

export function useOrbHold(onHold: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldRef = useRef(false);
  const onHoldRef = useRef(onHold);
  // Kept current in an effect, not during render. Writing to a ref while
  // rendering is a side effect, and React Compiler rejects it outright — which
  // is what had HOTTR.tv's lint red. The timer that reads this ref only ever
  // fires after commit, so an effect is soon enough.
  useEffect(() => {
    onHoldRef.current = onHold;
  });

  const clearHold = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const onPointerDown = useCallback(() => {
    heldRef.current = false;
    clearHold();
    timer.current = setTimeout(() => {
      heldRef.current = true;
      onHoldRef.current();
    }, HOLD_MS);
  }, [clearHold]);

  /**
   * End the press. Returns true when this press already meant Hottie — the
   * caller must not treat the release as a tap.
   */
  const finishPress = useCallback(() => {
    clearHold();
    const held = heldRef.current;
    heldRef.current = false;
    return held;
  }, [clearHold]);

  useEffect(() => clearHold, [clearHold]);

  return { onPointerDown, finishPress, clearHold };
}
